import React, { useEffect } from 'react';

import { BottomActions, Box, Button, Grid } from '@sunwisesoftware/sunwise-ui';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormDragnDropFile,
    ReactHookFormInput,
} from 'common/components/form/bootstrap';
import ReactHookFormInputDatePicker from 'common/components/form/bootstrap/ReactHookFormInputDatePicker';
import ShowErrors from 'common/components/ShowErrors';
import { FILE_ALLOWED_EXTENSIONS, MAX_SMALL_FILE_SIZE } from 'common/constants';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

const DataForm = ({ errors, handleClickSave, initialValues, isSaving }) => {
    const { t } = useTranslation();
    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => reset(initialValues), [initialValues]);

    const formValues = watch();

    return (
        <form onSubmit={handleSubmit(handleClickSave)}>
            <Grid container>
                <Grid
                    size={{ md: 10, xs: 18 }}
                    sx={{ order: { md: 1, xs: 2 } }}
                >
                    <ReactHookFormInput
                        control={control}
                        label={t('Name')}
                        name="first_name"
                        placeholder={t('Name', { count: 2 })}
                    />

                    <ReactHookFormInput
                        control={control}
                        label={t('Last name', { count: 2 })}
                        name="last_name"
                    />

                    <ReactHookFormInputDatePicker
                        control={control}
                        fullWidth
                        label={t('Date of birth')}
                        maxDate={new Date()}
                        name="birth_date"
                        placeholder="MM/DD/AAAA"
                    />

                    <ReactHookFormInput
                        autoComplete="off"
                        control={control}
                        label={t('Email')}
                        name="email"
                        readOnly
                        type="email"
                    />
                </Grid>

                <Grid size={{ md: 8, xs: 18 }} sx={{ order: { md: 2, xs: 1 } }}>
                    <Grid container justifyContent="center">
                        <Grid size={{ md: 8, xs: 18 }}>
                            <ReactHookFormDragnDropFile
                                accept={FILE_ALLOWED_EXTENSIONS.IMAGE}
                                avatarData={{
                                    image: formValues?.profile_image,
                                    name: `${formValues?.first_name} ${formValues?.last_name}`,
                                }}
                                control={control}
                                formats="jpg, jpeg, png"
                                id="profile_image"
                                maxSize={MAX_SMALL_FILE_SIZE}
                                name="profile_image"
                                setValue={setValue}
                                variant="profile"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <ShowErrors errors={errors} />

            <BottomActions>
                <Box pt={2}>
                    {!isEqual(initialValues, formValues) && (
                        <Button
                            type="submit"
                            disabled={isSaving}
                            sx={{ ml: 'auto' }}
                        >
                            {t('Save')}
                        </Button>
                    )}
                </Box>
            </BottomActions>
        </form>
    );
};

DataForm.propTypes = {
    errors: PropTypes.array,
    handleClickSave: PropTypes.func,
    initialValues: PropTypes.object,
    isSaving: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getErrorsSave,
    initialValues: selectors.getInitialValues,
    isSaving: selectors.getIsSaving,
});

const mapDispatchToProps = (dispatch) => ({
    handleClickSave: (values) => dispatch(actions.save(values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DataForm);
